export const cashDenominationSourceValues = [
  'suggested',
  'operatorConfirmed',
  'corrected',
  'countedBaseline',
] as const;
export type CashDenominationSource = (typeof cashDenominationSourceValues)[number];

export const cashDenominationConfidenceValues = [
  ...cashDenominationSourceValues,
  'mixed',
  'unknown',
] as const;
export type CashDenominationConfidence = (typeof cashDenominationConfidenceValues)[number];

export const cashDenominationCurrentStateStatusValues = [
  'counted',
  'projected',
  'partial',
  'unknown',
] as const;
export type CashDenominationCurrentStateStatus =
  (typeof cashDenominationCurrentStateStatusValues)[number];

export const cashDenominationCurrentStateSourceValues = [
  'cleanCount',
  'openingCount',
  'closingCount',
  'forceClosingCount',
  'purchasePayout',
  'purchaseCancellation',
  'transfer',
  'funding',
  'vaultIntake',
  'expense',
  'adjustment',
  'unknown',
] as const;
export type CashDenominationCurrentStateSource =
  (typeof cashDenominationCurrentStateSourceValues)[number];

export const cashDenominationValidationIssueCodeValues = [
  'invalidExpectedAmount',
  'invalidDenomination',
  'unsupportedDenomination',
  'invalidCount',
  'duplicateDenomination',
  'totalMismatch',
] as const;
export type CashDenominationValidationIssueCode =
  (typeof cashDenominationValidationIssueCodeValues)[number];

export interface CashDenominationRowDto {
  denom: number;
  count: number;
}

export interface CashDenominationValidationIssueDto {
  code: CashDenominationValidationIssueCode;
  message: string;
  index?: number;
  denominationMinor?: number;
  expectedAmountMinor?: number;
  denominationTotalMinor?: number;
}

export interface CashDenominationValidationResultDto {
  isValid: boolean;
  denominationTotalMinor: number;
  issues: CashDenominationValidationIssueDto[];
}

export interface SuggestCashDenominationsRequest {
  amountMinor: number;
}

export interface SuggestCashDenominationsResponse {
  amountMinor: number;
  rows: CashDenominationRowDto[];
  denominationTotalMinor: number;
  source: Extract<CashDenominationSource, 'suggested'>;
  confidence: Extract<CashDenominationConfidence, 'suggested'>;
  validation: CashDenominationValidationResultDto;
}

export interface CashDenominationAllocationDto {
  id: string;
  companyId: string;
  cashMovementId: string;
  cashLocationId: string;
  movementAmountMinor: number;
  currentRevisionId: string;
  currentRows: CashDenominationRowDto[];
  source: CashDenominationSource;
  confidence: CashDenominationConfidence;
  purchaseId?: string | null;
  effectiveAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface CashDenominationAllocationRevisionDto {
  id: string;
  companyId: string;
  allocationId: string;
  cashMovementId: string;
  source: CashDenominationSource;
  confidence: CashDenominationConfidence;
  rows: CashDenominationRowDto[];
  denominationTotalMinor: number;
  note: string | null;
  createdByUserId: string | null;
  effectiveAt: string;
  createdAt: string;
}

export interface GetCashDenominationAllocationByMovementRequest {
  companyId: string;
  cashMovementId: string;
}

export interface GetCashDenominationAllocationResponse {
  allocation: CashDenominationAllocationDto;
}

export interface ListCashDenominationAllocationRevisionsRequest {
  companyId: string;
  allocationId: string;
}

export interface ListCashDenominationAllocationRevisionsResponse {
  revisions: CashDenominationAllocationRevisionDto[];
}

export const cashDenominationBaselineTypeValues = ['cleanBaseline', 'varianceBaseline'] as const;
export type CashDenominationBaselineType = (typeof cashDenominationBaselineTypeValues)[number];

export interface CashDenominationCurrentStateDto {
  id: string | null;
  companyId: string;
  storeId: string | null;
  locationId: string | null;
  stationId: string | null;
  cashLocationId: string;
  status: CashDenominationCurrentStateStatus;
  confidence: CashDenominationCurrentStateStatus;
  source: CashDenominationCurrentStateSource;
  rows: CashDenominationRowDto[];
  totalMinor: number | null;
  ledgerBalanceMinor: number;
  differenceMinor: number | null;
  asOf: string | null;
  cashSessionId: string | null;
  lastMovementId: string | null;
  sourceSnapshotId: string | null;
  sourceBaselineId: string | null;
  stale: boolean;
  note: string;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface GetCashDenominationCurrentStateRequest {
  companyId: string;
  cashLocationId: string;
}

export interface GetCashDenominationCurrentStateResponse {
  currentState: CashDenominationCurrentStateDto;
}
